import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '../ui/Modal.js';
import { Button } from '../ui/Button.js';
import { LoadingState } from '../ui/LoadingState.js';
import { useToast } from '../ui/Toast.js';
import { api } from '../../lib/api.js';

export interface ContentPlanMarkdownModalProps {
  projectId: number;
  open: boolean;
  onClose: () => void;
}

// El sitemap en markdown, para llevárselo afuera: copiarlo a un chat, pegarlo
// en un documento, guardarlo. El plan estructurado sigue siendo la fuente —
// esto no vuelve a entrar.
export function ContentPlanMarkdownModal({ projectId, open, onClose }: ContentPlanMarkdownModalProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const [markdown, setMarkdown] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) { setMarkdown(null); setError(null); return; }
    let vivo = true;
    api.getText(`/api/projects/${projectId}/content-plan.md`)
      .then(md => { if (vivo) setMarkdown(md); })
      .catch((err: unknown) => { if (vivo) setError(err instanceof Error ? err.message : t('common.error')); });
    return () => { vivo = false; };
  }, [open, projectId, t]);

  function copiar() {
    if (!markdown) return;
    void navigator.clipboard.writeText(markdown)
      .then(() => toast.info(t('projects.contentPlan.markdown.copied')));
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('projects.contentPlan.markdown.title')}
      description={t('projects.contentPlan.markdown.description')}
      size="lg"
      footer={
        <>
          <Button variant="ghost" tone="muted" size="sm" onClick={onClose}>
            {t('common.close')}
          </Button>
          <a
            href={`/api/projects/${projectId}/content-plan.md?download=1`}
            download
            style={{ display: 'inline-flex' }}
          >
            <Button variant="outline" tone="muted" size="sm" disabled={!markdown}>
              {t('projects.contentPlan.markdown.download')}
            </Button>
          </a>
          <Button tone="cyan" size="sm" disabled={!markdown} onClick={copiar}>
            {t('projects.contentPlan.markdown.copy')}
          </Button>
        </>
      }
    >
      {error ? (
        <p style={{ margin: 0, fontSize: 'var(--evo-fs-body)', color: 'var(--evo-danger)', fontFamily: 'var(--evo-font)' }}>
          {error}
        </p>
      ) : markdown === null ? (
        <LoadingState size="sm" />
      ) : (
        <pre
          style={{
            margin: 0,
            maxHeight: '52vh',
            overflow: 'auto',
            padding: 'var(--evo-space-3)',
            border: '1px solid var(--evo-stroke)',
            borderRadius: 'var(--evo-r)',
            background: 'var(--evo-bg-2)',
            fontFamily: 'var(--evo-font-mono)',
            fontSize: 'var(--evo-fs-xs)',
            lineHeight: 1.7,
            color: 'var(--evo-fg-2)',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
        >
          {markdown}
        </pre>
      )}
    </Modal>
  );
}
